import GenericModal from '@components/Modal/Modal'
import { getHomeSearchResults } from 'helpers/homeSearch'
import { getNativeReaderHref } from 'helpers/nativeReaderHref'
import { useRouter } from 'next/router'
import {
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FC,
  type RefObject,
} from 'react'
import { FaSearch, FaTimesCircle } from 'react-icons/fa'
import SearchEmptyState from './Search/SearchEmptyState'
import SearchExampleChips from './Search/SearchExampleChips'
import SearchResultList from './Search/SearchResultList'
import { groupSearchResults } from './Search/searchResultGroups'
import { useSearchKeyboardNavigation } from './Search/useSearchKeyboardNavigation'

export type FilterResult = {
  name: string
  keywords: string
  url: string
  highlight?: boolean
}

type HomePageSearchModalProps = {
  isOpen: boolean
  triggerCloseModal: VoidFunction
}

/**
 * Desktop layout overrides for the shared modal: a command-palette style box near the
 * top of the screen instead of a tall box in the middle. Phones keep the full-screen
 * modal from GenericModal. `#home-search` beats the shared `.generic-modal` rules.
 */
const DESKTOP_MODAL_STYLE = `
  /*
   * The library gives the modal box overflow-y: auto. The box never scrolls itself,
   * but being a scroll box stops the sticky search bar from sticking while the modal
   * container scrolls. "clip" keeps the rounded corners without being a scroll box.
   */
  #home-search.generic-modal {
    overflow: clip;
  }

  @media screen and (min-width: 768px) {
    #home-search.generic-modal {
      width: 640px;
      min-height: 0;
      padding: 0;
      margin-top: 10vh;
      margin-bottom: 10vh;
      vertical-align: top;
    }
  }

  /*
   * Only with a mouse: cap the height and scroll the results inside the box.
   * Touch screens (phones, iPad) keep the natural height so the modal CONTAINER
   * scrolls. On iOS, body-scroll-lock (used by react-responsive-modal) blocks every
   * touch scroll unless the container itself can scroll, so an inner scroll area
   * would be frozen there. Same cause as the reading settings sheet bug,
   * see components/Modal/CHANGELOG.md (2026-10-28).
   */
  @media screen and (min-width: 768px) and (hover: hover) and (pointer: fine) {
    #home-search .search-modal-layout {
      max-height: 70vh;
    }
    #home-search .search-modal-results {
      overflow-y: auto;
    }
  }
`

/** Shown when a query has no results. */
const NoResults = ({
  onExampleSelect,
}: {
  onExampleSelect: (_query: string) => void
}) => (
  <div className="flex flex-col items-center px-4 py-10 text-center">
    <FaSearch size={28} className="mb-3 text-muted opacity-40" aria-hidden />
    <p className="text-base font-medium text-main">Sonuç bulunamadı...</p>
    <p className="mt-1 mb-5 max-w-[260px] text-sm text-muted">
      Sure adı, ayet numarası veya dua adıyla aramayı deneyin.
    </p>
    <SearchExampleChips onSelect={onExampleSelect} />
  </div>
)

/** Desktop-only hint row that explains the keyboard shortcuts. */
const KeyboardHints = () => (
  <div className="hidden items-center gap-4 border-t border-main px-5 py-2.5 text-xs text-muted md:flex">
    <span>
      <kbd className="font-sans">↑↓</kbd> gezin
    </span>
    <span>
      <kbd className="font-sans">Enter</kbd> aç
    </span>
    <span>
      <kbd className="font-sans">Esc</kbd> kapat
    </span>
  </div>
)

/**
 * Global search. Renders immediately from local metadata, including while offline.
 * Empty query: quick access tiles, example queries and often-read content.
 * With a query: results grouped as Kur'an-ı Kerim / Dualar / Araçlar, with ↑↓ + Enter
 * keyboard navigation on desktop.
 */
const HomePageSearchModal: FC<HomePageSearchModalProps> = ({
  isOpen,
  triggerCloseModal: onCloseModal,
}) => {
  const router = useRouter()
  const searchInput = useRef<HTMLInputElement>(null)
  const [word, setWord] = useState('')
  const hasQuery = word.trim().length > 0

  const resultGroups = useMemo(
    () => (hasQuery ? groupSearchResults(getHomeSearchResults(word)) : []),
    [hasQuery, word],
  )
  // The results in the order they are shown, for the keyboard selection.
  const orderedResults = resultGroups.flatMap((group) => group.results)

  const { activeIndex, setActiveIndex, resetActiveIndex, handleKeyDown } =
    useSearchKeyboardNavigation({
      resultCount: orderedResults.length,
      onOpenResult: (index) =>
        router.push(getNativeReaderHref(orderedResults[index].url)),
    })

  /** Sets the query and selects the first result again. */
  const updateQuery = (query: string) => {
    setWord(query)
    resetActiveIndex()
  }

  /** Updates the query as the user types. */
  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    updateQuery(event.target.value)
  }

  /** Fills the field with an example query and keeps the keyboard on the field. */
  const handleExampleSelect = (query: string) => {
    updateQuery(query)
    searchInput.current?.focus()
  }

  /** Closes the modal and clears the query, so it opens empty next time. */
  const closeAndReset = () => {
    onCloseModal()
    updateQuery('')
  }

  /** Picks what to show under the search field. */
  const renderContent = () => {
    if (!hasQuery) return <SearchEmptyState onExampleSelect={handleExampleSelect} />
    if (orderedResults.length === 0) {
      return <NoResults onExampleSelect={handleExampleSelect} />
    }
    return (
      <SearchResultList
        groups={resultGroups}
        activeIndex={activeIndex}
        onResultHover={setActiveIndex}
      />
    )
  }

  return (
    <GenericModal
      triggerCloseModal={closeAndReset}
      id="home-search"
      isOpen={isOpen}
      showCloseIcon={false}
      initialFocusRef={searchInput as RefObject<HTMLElement>}
    >
      <div className="search-modal-layout flex min-h-dvh flex-col bg-main md:min-h-0">
        <style>{DESKTOP_MODAL_STYLE}</style>

        <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-main bg-main px-4 py-3 md:px-5">
          <div className="relative flex-1">
            <label htmlFor="searchInput" className="sr-only">
              Arama yapın
            </label>
            <FaSearch
              size={15}
              className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted"
              aria-hidden
            />
            <input
              ref={searchInput}
              id="searchInput"
              name="searchInput"
              type="search"
              inputMode="search"
              enterKeyHint="go"
              autoComplete="off"
              placeholder="Sure, ayet veya dua ara..."
              value={word}
              onChange={handleSearchChange}
              onKeyDown={handleKeyDown}
              className="h-11 w-full rounded-xl border border-main bg-surface-subtle pl-10 pr-10 text-base text-main outline-hidden transition-colors placeholder:text-muted focus:border-primary-400 focus:bg-main focus:ring-2 focus:ring-primary-300/30 [&::-webkit-search-cancel-button]:appearance-none"
            />
            {word && (
              <button
                type="button"
                onClick={() => handleExampleSelect('')}
                aria-label="Aramayı temizle"
                className="absolute right-1 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center text-muted hover:text-main"
              >
                <FaTimesCircle size={16} aria-hidden />
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={closeAndReset}
            className="px-1 py-2 text-[15px] font-medium text-primary active:opacity-70 md:hidden"
          >
            Vazgeç
          </button>
        </div>

        <div className="search-modal-results flex-1 px-4 pt-4 pb-8 md:px-5 md:pb-5">
          {renderContent()}
        </div>

        <KeyboardHints />
      </div>
    </GenericModal>
  )
}

export default HomePageSearchModal
